/**
 * Pro Features Component
 *
 * Displays premium features available in the Pro version.
 *
 * @since 1.0.0
 */

import {
	AdjustmentsHorizontalIcon,
	ArrowPathIcon,
	BoltIcon,
	CubeIcon,
	LifebuoyIcon,
	PaintBrushIcon,
	ShoppingBagIcon,
	SparklesIcon,
} from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { TabHeader } from '@redfoxwp/core-components/components/tab-header';
import { __ } from '@wordpress/i18n';

import type { ProFeature } from './types';


/**
 * Pro Features component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const ProFeatures = (): JSX.Element => {
	const proFeatures: ProFeature[] = [
		{
			icon: <AdjustmentsHorizontalIcon width={32} height={32} />,
			title: __('Rule Builder', 'redfox-thank-you'),
			description: __(
				'Set up smart conditional logic to display personalized thank you pages based on cart contents, order value, customer type, payment method, and shipping location.',
				'redfox-thank-you',
			),
		},
		{
			icon: <CubeIcon width={32} height={32} />,
			title: __('6 Pro Blocks', 'redfox-thank-you'),
			description: __(
				'Expand your toolkit with premium blocks including Products Slider, Products Grid, Social Media, Banner and more.',
				'redfox-thank-you',
			),
		},
		{
			icon: <ShoppingBagIcon width={32} height={32} />,
			title: __('Thank You Page Per Product', 'redfox-thank-you'),
			description: __(
				'Create targeted thank you pages for individual products or categories to deliver tailored post-purchase experiences that increase customer satisfaction.',
				'redfox-thank-you',
			),
		},
		{
			icon: <PaintBrushIcon width={32} height={32} />,
			title: __('Advanced Styling Options', 'redfox-thank-you'),
			description: __(
				'Fine-tune every detail with extended styling controls including custom spacing, box shadows, gradient backgrounds, and device-specific responsive settings.',
				'redfox-thank-you',
			),
		},
		{
			icon: <BoltIcon width={32} height={32} />,
			title: __('Google Fonts Integration', 'redfox-thank-you'),
			description: __(
				'Elevate your brand presence with access to 1,000+ Google Fonts, complete with weight and style variants for perfect typographic consistency.',
				'redfox-thank-you',
			),
		},
		{
			icon: <LifebuoyIcon width={32} height={32} />,
			title: __('Priority Support', 'redfox-thank-you'),
			description: __(
				'Receive fast, expert assistance from our dedicated support team with priority response times and personalized guidance for your store.',
				'redfox-thank-you',
			),
		},
		{
			icon: <ArrowPathIcon width={32} height={32} />,
			title: __('More coming soon', 'redfox-thank-you'),
			description: __(
				'Our roadmap is packed with exciting features including analytics dashboards, A/B testing, and email integrations. Pro users get early access to all new releases.',
				'redfox-thank-you',
			),
		},
	];

	const handleUpgradeClick = () => {
		window.open('https://www.redfoxwp.com/plugin/redfox-thank-you/', '_blank');
	};

	return (
		<Flex direction="column" gap="20px" fullWidth>
			<FlexItem>
				<TabHeader
					title={__('Pro Features', 'redfox-thank-you')}
					description={__(
						'Unlock powerful features to create high-converting thank you pages.',
						'redfox-thank-you',
					)}
				/>
			</FlexItem>

			{/* Upgrade CTA Card */}
			<Card
				styleProps={{
					radius: '10px',
					padding: '30px',
					background: '#fff',
				}}
			>
				<Flex direction="column" gap="20px" align="center">
					<SparklesIcon width={48} height={48} color={colors.primary[400]} />
					<Flex direction="column" gap="8px" align="center">
						<h2
							style={{
								margin: '0 0 15px',
								fontSize: '2em',
								fontWeight: 600,
							}}
						>
							{__('Upgrade to Pro', 'redfox-thank-you')}
						</h2>
						<p
							style={{
								margin:  '0 0 20px',
								textAlign: 'center',
								maxWidth: '500px',
							}}
						>
							{__(
								'Get access to advanced blocks, conditional logic, styling options, and more to create thank you pages that convert.',
								'redfox-thank-you',
							)}
						</p>
					</Flex>
					<Button
						variant="solid"
						onClick={handleUpgradeClick}
					>
						{__('Get Pro Now', 'redfox-thank-you')}
					</Button>
				</Flex>
			</Card>

			{/* Features Grid */}
			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
					gap: '20px',
				}}
			>
				{proFeatures.map((feature, index) => (
					<Card
						key={index}
						styleProps={{
							radius: '10px',
							padding: '24px',
							background: '#fff',
						}}
					>
						<Flex direction="column" gap="12px">
							<div
								style={{
									color: colors.primary[500],
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									width: '56px',
									height: '56px',
									borderRadius: '12px',
									backgroundColor: colors.primary[50],
								}}
							>
								{feature.icon}
							</div>
							<h3
								style={{
									margin: 0,
									fontSize: '1.1em',
									fontWeight: 600,
									color: colors.gray[900],
								}}
							>
								{feature.title}
							</h3>
							<p
								style={{
									margin: 0,
									fontSize: '0.95em',
									color: colors.gray[600],
									lineHeight: 1.5,
								}}
							>
								{feature.description}
							</p>
						</Flex>
					</Card>
				))}
			</div>

			{/* Bottom CTA */}
			<Card
				styleProps={{
					radius: '10px',
					padding: '24px',
					background: '#fff',
				}}
			>
				<Flex direction="row" gap="20px" align="center" justify="space-between">
					<Flex direction="column" gap="4px">
						<h3
							style={{
								margin: 0,
								fontSize: '1.1em',
								fontWeight: 600,
								color: colors.gray[900],
							}}
						>
							{__('Ready to boost your conversions?', 'redfox-thank-you')}
						</h3>
						<p
							style={{
								margin: 0,
								fontSize: '0.95em',
								color: colors.gray[600],
							}}
						>
							{__(
								'Join thousands of store owners who increased their sales with RedFox Thank You Pro.',
								'redfox-thank-you',
							)}
						</p>
					</Flex>
					<Button variant="solid" onClick={handleUpgradeClick}>
						{__('Upgrade Now', 'redfox-thank-you')}
					</Button>
				</Flex>
			</Card>
		</Flex>
	);
};
